/**
 * Selection range
 *
 * Provides the chain of ranges the editor uses for "expand selection"
 * (⌥⇧→ / Ctrl+W). Some IDEs (LSP4IJ in IntelliJ in particular) also
 * consult the *smallest* range here when deciding what double-click
 * should select for a TextMate-backed language. So the chain MUST start
 * at a single word — even when the cursor is inside a string or comment.
 *
 * Chain layout, smallest → largest:
 *   1. word under cursor   ([A-Za-z0-9_]+ scan on source text — works
 *                            inside comments and strings too)
 *   2. dotted-path wrapping the word (`Orders.Inner.Leaf`)
 *   3. string content (no quotes), then string-with-quotes (when in a string)
 *   4. comment content (when in a comment)
 *   5. line statement
 *   6. each enclosing `{ }` content, then `{ }` with braces
 *   7. each enclosing declaration up to the file
 *
 * Ranges are de-duplicated and sorted by size so the parent chain grows
 * monotonically.
 */

import type { Position, Range, SelectionRange } from "vscode-languageserver";
import type {
  SurfaceDecl,
  InterfaceDecl,
  ModuleDecl,
  ProcessDecl,
  TopLevelDecl,
  ViewDeclV2,
} from "@archlang/engine";
import type { Token } from "@archlang/engine";
import type { Workspace, FileState } from "../workspace/workspace.js";
import { toLspRange } from "../lsp-types.js";
import { isIdentChar } from "../workspace/ident-chars.js";

const WORD_CHAR = { test: (ch: string) => isIdentChar(ch) };

export function provideSelectionRanges(
  workspace: Workspace,
  uri: string,
  positions: readonly Position[]
): SelectionRange[] {
  const file = workspace.getFile(uri);
  if (!file) return positions.map((p) => ({ range: { start: p, end: p } }));
  return positions.map((p) => buildRangeChain(file, p));
}

function buildRangeChain(file: FileState, position: Position): SelectionRange {
  const text = file.text;
  const offset = offsetAt(text, position.line, position.character);
  const ranges: Range[] = [];

  // 1. Word under cursor — scan source text directly. This works
  //    uniformly in code, inside strings, and inside comments.
  const wordRange = findWordAt(text, offset);
  if (wordRange) ranges.push(wordRange);

  // 2. Dotted-path wrapping the word (`Orders.Inner.Leaf`).
  if (wordRange) {
    const dotted = expandDottedPath(text, wordRange);
    if (dotted) ranges.push(dotted);
  }

  // 3. String content + string-with-quotes (when cursor is inside a STRING token).
  const strInfo = stringRangesAt(file.tokens, offset);
  if (strInfo) {
    if (strInfo.content) ranges.push(strInfo.content);
    ranges.push(strInfo.full);
  }

  // 4. Comment content (when cursor is inside a comment trivia).
  const commentRange = commentRangeAt(file.tokens, offset);
  if (commentRange) ranges.push(commentRange);

  // 5. Line-level statement (trimmed).
  const lineRange = computeLineRange(text, position.line);
  if (lineRange) ranges.push(lineRange);

  // 6. Brace nesting (innermost outward).
  for (const r of computeBraceChain(file.tokens, offset)) {
    ranges.push(r);
  }

  // 7. Declarations.
  for (const top of file.file.declarations) {
    addDeclarationCandidates(top, position, ranges);
  }

  // De-dup and sort largest → smallest so the chain we build ends with
  // the smallest at the head.
  const unique = dedup(ranges).sort((a, b) => rangeSize(b) - rangeSize(a));

  let current: SelectionRange | undefined;
  for (const r of unique) {
    current = { range: r, parent: current };
  }
  return current ?? { range: { start: position, end: position } };
}

// =============================================================================
// Word-at-cursor (text-level)
// =============================================================================

function findWordAt(text: string, offset: number): Range | null {
  const o = Math.min(Math.max(offset, 0), text.length);
  let start = o;
  while (start > 0 && WORD_CHAR.test(text[start - 1]!)) start--;
  let end = o;
  while (end < text.length && WORD_CHAR.test(text[end]!)) end++;
  if (start === end) return null;
  return { start: positionAt(text, start), end: positionAt(text, end) };
}

function expandDottedPath(text: string, word: Range): Range | null {
  const startOff = offsetAt(text, word.start.line, word.start.character);
  const endOff = offsetAt(text, word.end.line, word.end.character);
  let s = startOff;
  let e = endOff;
  let grew = false;
  // Extend left through `.<word>`
  while (s > 1 && text[s - 1] === ".") {
    let probe = s - 2;
    while (probe >= 0 && WORD_CHAR.test(text[probe]!)) probe--;
    if (probe + 1 === s - 1) break;
    s = probe + 1;
    grew = true;
  }
  // Extend right through `.<word>`
  while (e < text.length && text[e] === ".") {
    let probe = e + 1;
    while (probe < text.length && WORD_CHAR.test(text[probe]!)) probe++;
    if (probe === e + 1) break;
    e = probe;
    grew = true;
  }
  if (!grew) return null;
  return { start: positionAt(text, s), end: positionAt(text, e) };
}

// =============================================================================
// String / comment helpers
// =============================================================================

interface StringRanges {
  /** Range of the inner string content, exclusive of quotes. Null if empty. */
  content: Range | null;
  /** Range of the full string token, including quotes. */
  full: Range;
}

function stringRangesAt(tokens: readonly Token[], offset: number): StringRanges | null {
  for (const t of tokens) {
    if (t.type !== "STRING") continue;
    if (offset < t.span.start.offset || offset > t.span.end.offset) continue;
    const full = toLspRange(t.span);
    // Inner content excludes the leading and trailing quote.
    const innerStart = t.span.start.offset + 1;
    const innerEnd = t.span.end.offset - 1;
    if (innerEnd <= innerStart) return { content: null, full };
    return {
      content: {
        start: { line: full.start.line, character: full.start.character + 1 },
        end: { line: full.end.line, character: full.end.character - 1 },
      },
      full,
    };
  }
  return null;
}

function commentRangeAt(tokens: readonly Token[], offset: number): Range | null {
  for (const t of tokens) {
    for (const tr of t.leadingTrivia) {
      if (tr.kind !== "lineComment" && tr.kind !== "blockComment") continue;
      if (offset < tr.span.start.offset || offset > tr.span.end.offset) continue;
      return toLspRange(tr.span);
    }
    for (const tr of t.trailingTrivia) {
      if (tr.kind !== "lineComment" && tr.kind !== "blockComment") continue;
      if (offset < tr.span.start.offset || offset > tr.span.end.offset) continue;
      return toLspRange(tr.span);
    }
  }
  return null;
}

// =============================================================================
// Line range
// =============================================================================

function computeLineRange(text: string, line: number): Range | null {
  const lines = text.split("\n");
  if (line < 0 || line >= lines.length) return null;
  const lineText = lines[line]!;
  const trimmedStart = lineText.search(/\S/);
  if (trimmedStart < 0) return null;
  const trimmedEnd = lineText.replace(/\s+$/, "").length;
  return {
    start: { line, character: trimmedStart },
    end: { line, character: trimmedEnd },
  };
}

// =============================================================================
// Brace nesting
// =============================================================================

function computeBraceChain(tokens: readonly Token[], cursorOffset: number): Range[] {
  const out: Range[] = [];
  const stack: number[] = [];
  for (let i = 0; i < tokens.length; i++) {
    const t = tokens[i]!;
    if (t.type === "EOF") break;
    if (t.type === "LBRACE") {
      stack.push(i);
      continue;
    }
    if (t.type === "RBRACE") {
      const openIdx = stack.pop();
      if (openIdx == null) continue;
      const open = tokens[openIdx]!;
      if (open.span.end.offset <= cursorOffset && cursorOffset <= t.span.start.offset) {
        out.push({
          start: toLspPos(open.span.end),
          end: toLspPos(t.span.start),
        });
        out.push({
          start: toLspPos(open.span.start),
          end: toLspPos(t.span.end),
        });
      }
    }
  }
  return out;
}

// =============================================================================
// Declaration candidates
// =============================================================================

function addDeclarationCandidates(
  top: TopLevelDecl,
  pos: Position,
  out: Range[]
): void {
  switch (top.kind) {
    case "module":
      addModuleCandidates(top.decl, pos, out);
      break;
    case "process":
      addProcessCandidates(top.decl, pos, out);
      break;
    case "view":
      addViewCandidates(top.decl, pos, out);
      break;
    case "type":
      if (top.decl.source) {
        const r = toLspRange(top.decl.source);
        if (containsPos(r, pos)) out.push(r);
      }
      break;
    case "extension": {
      // v0.8.2 §4.16: expand-selection wraps the `in Target { … }` block, then its
      // interfaces / sub-modules at declaration granularity.
      if (top.decl.source) {
        const r = toLspRange(top.decl.source);
        if (containsPos(r, pos)) out.push(r);
      }
      for (const iface of top.decl.body.interfaces) {
        if (iface.source) {
          const r = toLspRange(iface.source);
          if (containsPos(r, pos)) out.push(r);
        }
      }
      for (const sub of top.decl.body.modules) addModuleCandidates(sub, pos, out);
      break;
    }
  }
}

function addModuleCandidates(m: ModuleDecl, pos: Position, out: Range[]): void {
  if (!m.source) return;
  const r = toLspRange(m.source);
  if (!containsPos(r, pos)) return;
  out.push(r);
  for (const f of m.body.surfaces) addSurfaceCandidates(f, pos, out);
  for (const i of m.body.interfaces) addInterfaceCandidates(i, pos, out);
  for (const sub of m.body.modules) addModuleCandidates(sub, pos, out);
}

function addSurfaceCandidates(f: SurfaceDecl, pos: Position, out: Range[]): void {
  if (!f.source) return;
  const r = toLspRange(f.source);
  if (!containsPos(r, pos)) return;
  out.push(r);
  for (const sub of f.body.surfaces) addSurfaceCandidates(sub, pos, out);
  for (const i of f.body.interfaces) addInterfaceCandidates(i, pos, out);
}

function addInterfaceCandidates(i: InterfaceDecl, pos: Position, out: Range[]): void {
  if (!i.source) return;
  const r = toLspRange(i.source);
  if (containsPos(r, pos)) out.push(r);
}

function addProcessCandidates(p: ProcessDecl, pos: Position, out: Range[]): void {
  if (!p.source) return;
  const r = toLspRange(p.source);
  if (containsPos(r, pos)) out.push(r);
}

function addViewCandidates(v: ViewDeclV2, pos: Position, out: Range[]): void {
  if (!v.source) return;
  const r = toLspRange(v.source);
  if (containsPos(r, pos)) out.push(r);
}

// =============================================================================
// Helpers
// =============================================================================

function containsPos(r: Range, p: Position): boolean {
  const startLE =
    r.start.line < p.line ||
    (r.start.line === p.line && r.start.character <= p.character);
  const endGE =
    r.end.line > p.line ||
    (r.end.line === p.line && r.end.character >= p.character);
  return startLE && endGE;
}

function rangeSize(r: Range): number {
  return (r.end.line - r.start.line) * 100000 + (r.end.character - r.start.character);
}

function dedup(ranges: readonly Range[]): Range[] {
  const seen = new Set<string>();
  const out: Range[] = [];
  for (const r of ranges) {
    const key = `${r.start.line}:${r.start.character}-${r.end.line}:${r.end.character}`;
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(r);
  }
  return out;
}

function offsetAt(text: string, line: number, character: number): number {
  let cur = 0;
  let lineStart = 0;
  for (let i = 0; i < text.length; i++) {
    if (cur === line) break;
    if (text[i] === "\n") {
      cur++;
      lineStart = i + 1;
    }
  }
  return Math.min(text.length, lineStart + character);
}

function positionAt(text: string, offset: number): Position {
  const o = Math.min(Math.max(offset, 0), text.length);
  let line = 0;
  let lineStart = 0;
  for (let i = 0; i < o; i++) {
    if (text[i] === "\n") {
      line++;
      lineStart = i + 1;
    }
  }
  return { line, character: o - lineStart };
}

function toLspPos(p: { line: number; column: number }): Position {
  return { line: p.line - 1, character: p.column - 1 };
}
